/**
 * Academic Calendar — SINGLE SOURCE OF TRUTH for semester dates.
 *
 * Authoritative storage lives in the `academic_calendar_windows` DB table.
 * This module hydrates that table into an in-memory store on app boot, then
 * exposes synchronous resolvers that every surface (class card, detail
 * dialog, registration form, interest form, calendar, admin editor preview,
 * confirmation emails, MAVIS narration) can call.
 *
 * The FALLBACK constants below are only used before the DB has responded or
 * when a semester has no rows yet. Never treat them as the permanent truth.
 *
 * Edit dates in: Admin → Settings → Academic Calendar
 */

import { supabase } from "@/integrations/supabase/client";
import { useEffect, useSyncExternalStore } from "react";

export type SemesterId = "fall_2026" | "spring_2027" | (string & {});

export type WeekdayName =
  | "Sunday" | "Monday" | "Tuesday" | "Wednesday"
  | "Thursday" | "Friday" | "Saturday";

export interface WeekdayWindow {
  start: string; // YYYY-MM-DD
  end: string;   // YYYY-MM-DD
  weeks?: number;
  academicYear?: number;
}

export interface SemesterConfig {
  label: string;
  academicYear?: number;
  weekdays: Partial<Record<WeekdayName, WeekdayWindow>>;
}

const FALLBACK_CALENDAR: Record<string, SemesterConfig> = {
  fall_2026: {
    label: "Fall Semester",
    academicYear: 2026,
    weekdays: {
      Monday:    { start: "2026-08-31", end: "2026-11-16", weeks: 12, academicYear: 2026 },
      Tuesday:   { start: "2026-09-01", end: "2026-11-17", weeks: 12, academicYear: 2026 },
      Wednesday: { start: "2026-09-02", end: "2026-11-18", weeks: 12, academicYear: 2026 },
      Thursday:  { start: "2026-09-03", end: "2026-11-19", weeks: 12, academicYear: 2026 },
      Friday:    { start: "2026-09-04", end: "2026-11-20", weeks: 12, academicYear: 2026 },
    },
  },
  spring_2027: {
    label: "Spring Semester",
    academicYear: 2027,
    weekdays: {},
  },
};

// Live, DB-hydrated calendar. Starts as fallback; replaced when DB loads.
let CURRENT_CALENDAR: Record<string, SemesterConfig> = structuredClone(FALLBACK_CALENDAR);

export const ACADEMIC_CALENDAR = new Proxy({} as Record<string, SemesterConfig>, {
  get: (_t, key: string) => CURRENT_CALENDAR[key],
  ownKeys: () => Reflect.ownKeys(CURRENT_CALENDAR),
  getOwnPropertyDescriptor: (_t, key: string) =>
    Object.getOwnPropertyDescriptor(CURRENT_CALENDAR, key),
  has: (_t, key: string) => key in CURRENT_CALENDAR,
});

const WEEKDAY_INDEX: Record<WeekdayName, number> = {
  Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
  Thursday: 4, Friday: 5, Saturday: 6,
};

const WEEKDAY_ORDER: WeekdayName[] = [
  "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday",
];

const SHORT_MONTH = [
  "Jan", "Feb", "Mar", "Apr", "May", "June",
  "July", "Aug", "Sept", "Oct", "Nov", "Dec",
];

function parseISO(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** "2026-09-04" -> "Sept 4" */
export function formatShortDate(iso: string): string {
  const d = parseISO(iso);
  return `${SHORT_MONTH[d.getMonth()]} ${d.getDate()}`;
}

/** "2026-09-04" -> "September 4, 2026" */
export function formatLongDate(iso: string): string {
  const d = parseISO(iso);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

/** Inclusive week count between two ISO dates on the same weekday. */
export function weeksBetween(startIso: string, endIso: string): number {
  const ms = parseISO(endIso).getTime() - parseISO(startIso).getTime();
  if (ms < 0) return 0;
  return Math.round(ms / (7 * 24 * 60 * 60 * 1000)) + 1;
}

export interface ResolvedWeekdayWindow extends WeekdayWindow {
  weekday: WeekdayName;
  weekdayIndex: number;
  label: string;
  weeks: number;
  rangeShort: string; // "Sept 4 – Nov 20"
  rangeLong: string;  // "September 4 – November 20, 2026"
}

export function getSemesterWindowForWeekday(
  semester: SemesterId,
  weekday: WeekdayName,
): ResolvedWeekdayWindow | null {
  const cfg = CURRENT_CALENDAR[semester];
  const win = cfg?.weekdays[weekday];
  if (!win) return null;
  const weeks = win.weeks ?? weeksBetween(win.start, win.end);
  const yr = win.academicYear ?? cfg.academicYear;
  const s = parseISO(win.start);
  const e = parseISO(win.end);
  const rangeLong = `${s.toLocaleDateString("en-US", { month: "long", day: "numeric" })} – ${e.toLocaleDateString("en-US", { month: "long", day: "numeric" })}${yr ? `, ${yr}` : ""}`;
  return {
    ...win,
    weekday,
    weekdayIndex: WEEKDAY_INDEX[weekday],
    label: cfg.label,
    weeks,
    rangeShort: `${formatShortDate(win.start)} – ${formatShortDate(win.end)}`,
    rangeLong,
  };
}

export interface ClassSemesterDescription {
  semester: SemesterId;
  label: string;
  lines: Array<{ weekday: WeekdayName; text: string; weeks: number; rangeLong: string }>;
  summary: string;
}

export function describeSemesterForClass(
  semester: SemesterId,
  weekdays: WeekdayName[],
): ClassSemesterDescription | null {
  const cfg = CURRENT_CALENDAR[semester];
  if (!cfg) return null;
  const unique = Array.from(new Set(weekdays))
    .filter((w): w is WeekdayName => Boolean(cfg.weekdays[w]))
    .sort((a, b) => WEEKDAY_INDEX[a] - WEEKDAY_INDEX[b]);
  if (unique.length === 0) return null;

  const lines = unique.map((w) => {
    const win = getSemesterWindowForWeekday(semester, w)!;
    return {
      weekday: w,
      weeks: win.weeks,
      rangeLong: win.rangeLong,
      text: unique.length === 1 ? win.rangeShort : `${w}s: ${win.rangeShort}`,
    };
  });

  const summary =
    unique.length === 1
      ? `${cfg.label} · ${lines[0].text} · ${lines[0].weeks} Weeks`
      : `${cfg.label} · ${lines.map((l) => l.text).join(" · ")}`;

  return { semester, label: cfg.label, lines, summary };
}

export function formatMonthlyPeriod(monthName: string): string {
  return monthName.trim();
}

export function semesterIdFromLabel(label: string | null | undefined): SemesterId | null {
  const s = (label ?? "").toLowerCase();
  if (s.includes("fall")) return "fall_2026";
  if (s.includes("spring")) return "spring_2027";
  return null;
}

/** How many calendar months a semester spans (max across configured weekdays). */
export function getSemesterMonthCount(semester: SemesterId): number | null {
  const cfg = CURRENT_CALENDAR[semester];
  if (!cfg) return null;
  const wins = Object.values(cfg.weekdays ?? {}).filter(Boolean) as WeekdayWindow[];
  if (wins.length === 0) return null;
  const starts = wins.map((w) => parseISO(w.start).getTime());
  const ends = wins.map((w) => parseISO(w.end).getTime());
  const s = new Date(Math.min(...starts));
  const e = new Date(Math.max(...ends));
  return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
}

// ────────────────────────────────────────────────────────────────────────────
// DB hydration + subscription
// ────────────────────────────────────────────────────────────────────────────

export interface AcademicCalendarWindow {
  id: string;
  semester_key: string;
  display_label: string;
  academic_year: number;
  weekday: WeekdayName;
  start_date: string;
  end_date: string;
  instructional_weeks: number;
  is_active: boolean;
  updated_at?: string;
}

const listeners = new Set<() => void>();
let hydrated = false;
let hydrating: Promise<void> | null = null;
let snapshotVersion = 0;

function rebuildFromRows(rows: AcademicCalendarWindow[]) {
  const next: Record<string, SemesterConfig> = structuredClone(FALLBACK_CALENDAR);
  // Rows override fallback: clear a semester's weekdays first time we see it.
  const cleared = new Set<string>();
  for (const r of rows) {
    if (!r.is_active) continue;
    if (!cleared.has(r.semester_key)) {
      next[r.semester_key] = {
        label: r.display_label,
        academicYear: r.academic_year,
        weekdays: {},
      };
      cleared.add(r.semester_key);
    }
    next[r.semester_key].weekdays[r.weekday] = {
      start: r.start_date,
      end: r.end_date,
      weeks: r.instructional_weeks,
      academicYear: r.academic_year,
    };
  }
  CURRENT_CALENDAR = next;
  snapshotVersion++;
  listeners.forEach((l) => l());
}

export async function hydrateAcademicCalendar(force = false): Promise<void> {
  if (hydrated && !force) return;
  if (hydrating) return hydrating;
  hydrating = (async () => {
    const { data, error } = await supabase
      .from("academic_calendar_windows")
      .select("*")
      .order("semester_key")
      .order("weekday");
    if (!error && data) rebuildFromRows(data as unknown as AcademicCalendarWindow[]);
    hydrated = true;
    hydrating = null;
  })();
  return hydrating;
}

export function subscribeAcademicCalendar(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() { return snapshotVersion; }
function getServerSnapshot() { return 0; }

/** React hook — triggers re-render on hydration/update. */
export function useAcademicCalendar() {
  useSyncExternalStore(
    (cb) => subscribeAcademicCalendar(cb),
    getSnapshot,
    getServerSnapshot,
  );
  useEffect(() => {
    hydrateAcademicCalendar();
    const ch = supabase
      .channel("academic-calendar-windows")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "academic_calendar_windows" },
        () => hydrateAcademicCalendar(true),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);
  return CURRENT_CALENDAR;
}

// Kick off hydration eagerly at module load so non-hook callers still get
// fresh values without waiting for a component to mount.
if (typeof window !== "undefined") {
  hydrateAcademicCalendar().catch(() => { /* silent — fallback stays in place */ });
}

export { WEEKDAY_INDEX, WEEKDAY_ORDER, FALLBACK_CALENDAR };
